import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAuditLog, type AuditRow, type AuditQuery } from '@/api/audit';
import { effectiveTimezone } from '@/lib/datetime';

const SECTION_OPTIONS = [
  '', 'apps', 'campaigns', 'ads', 'pwa', 'offers', 'firebase', 'tracker',
  'notifications', 'settings', 'users', 'generate', 'variants', 'devices',
];
const METHOD_OPTIONS = ['', 'POST', 'PATCH', 'PUT', 'DELETE'];
const PAGE_SIZES = [25, 50, 100, 200];

function formatTs(ts: number): string {
  return new Date(ts * 1000).toLocaleString(undefined, {
    timeZone: effectiveTimezone(),
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function methodPill(method: string) {
  const color =
    method === 'DELETE' ? 'bg-danger-soft text-danger' :
    method === 'POST'   ? 'bg-success-soft text-success' :
    method === 'PATCH' || method === 'PUT' ? 'bg-info-soft text-info' :
    'bg-surface-muted text-content-muted';
  return <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${color}`}>{method}</span>;
}

function statusPill(status: number | null) {
  if (!status) return <span className="text-content-subtle">—</span>;
  const color =
    status >= 500 ? 'bg-danger-soft text-danger' :
    status >= 400 ? 'bg-warning-soft text-warning' :
    status >= 300 ? 'bg-warning-soft text-warning' :
                    'bg-success-soft text-success';
  return <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${color}`}>{status}</span>;
}

interface FieldChange { field: string; from: unknown; to: unknown }

function parseChanges(raw: string | null): FieldChange[] | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0) return parsed as FieldChange[];
  } catch { /* not JSON — fall back to body */ }
  return null;
}

function fmtVal(v: unknown): string {
  if (v === null || v === undefined || v === '') return '∅';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function ChangesTable({ changes }: { changes: FieldChange[] }) {
  return (
    <table className="w-full text-[11px] border border-border rounded overflow-hidden mb-2">
      <thead className="bg-surface text-content-subtle">
        <tr>
          <th className="px-2 py-1 text-left font-medium">Field</th>
          <th className="px-2 py-1 text-left font-medium">From → To</th>
        </tr>
      </thead>
      <tbody>
        {changes.map((ch) => (
          <tr key={ch.field} className="border-t border-border">
            <td className="px-2 py-1 font-mono text-content">{ch.field}</td>
            <td className="px-2 py-1">
              <span className="font-mono text-content-subtle line-through">{fmtVal(ch.from)}</span>
              <span className="mx-1 text-content-subtle">→</span>
              <span className="font-mono text-success">{fmtVal(ch.to)}</span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Row({ row }: { row: AuditRow }) {
  const [expanded, setExpanded] = useState(false);
  const changes = parseChanges(row.changes);
  const hasDetails = !!(changes || row.body || row.entity_id || row.ip);
  return (
    <>
      <tr className="border-t border-border hover:bg-surface-muted">
        <td className="px-3 py-2 whitespace-nowrap text-content-subtle tabular-nums">{formatTs(row.ts)}</td>
        <td className="px-3 py-2 whitespace-nowrap">
          <div className="text-content">{row.admin_email || <span className="text-content-subtle">system</span>}</div>
        </td>
        <td className="px-3 py-2 whitespace-nowrap">{methodPill(row.method)}</td>
        <td className="px-3 py-2">
          <div className="text-content">
            {row.action ? row.action : <span className="text-content-subtle">{row.path}</span>}
          </div>
          {row.action && <div className="text-xs text-content-subtle font-mono truncate">{row.path}</div>}
        </td>
        <td className="px-3 py-2 whitespace-nowrap">
          {row.section ? <span className="px-1.5 py-0.5 rounded text-[10px] bg-surface-muted text-content-muted">{row.section}</span> : <span className="text-content-subtle">—</span>}
        </td>
        <td className="px-3 py-2 whitespace-nowrap">{statusPill(row.status)}</td>
        <td className="px-3 py-2 whitespace-nowrap text-right">
          {hasDetails && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="text-info hover:underline text-xs"
            >
              {expanded ? 'hide' : 'details'}
            </button>
          )}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-surface-muted border-t border-border">
          <td colSpan={7} className="px-3 py-2 text-xs">
            <div className="flex flex-wrap gap-x-6 gap-y-1 mb-2 text-content-muted">
              {row.entity_type && <span><span className="text-content-subtle">entity:</span> {row.entity_type}{row.entity_id ? ` #${row.entity_id}` : ''}</span>}
              {row.ip && <span><span className="text-content-subtle">ip:</span> {row.ip}</span>}
              {row.admin_id && <span><span className="text-content-subtle">admin_id:</span> {row.admin_id}</span>}
            </div>
            {changes ? (
              <ChangesTable changes={changes} />
            ) : row.body ? (
              <pre className="bg-surface border border-border rounded p-2 overflow-x-auto text-[11px] text-content">
                {row.body}
              </pre>
            ) : null}
          </td>
        </tr>
      )}
    </>
  );
}

export function AuditLog() {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [search, setSearch] = useState('');
  const [section, setSection] = useState('');
  const [method, setMethod] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fromTs = dateFrom ? Math.floor(new Date(`${dateFrom}T00:00:00`).getTime() / 1000) : undefined;
  const toTs = dateTo ? Math.floor(new Date(`${dateTo}T23:59:59`).getTime() / 1000) : undefined;

  const query: AuditQuery = {
    page, limit,
    search: search || undefined,
    section: section || undefined,
    method: method || undefined,
    from: fromTs,
    to: toTs,
  };

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['audit-log', query],
    queryFn: () => getAuditLog(query),
    placeholderData: (prev) => prev,
  });

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const rows = data?.rows ?? [];

  const onFilterChange = (set: (v: string) => void) => (v: string) => { set(v); setPage(1); };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">Audit log</h1>
        <div className="text-sm text-content-subtle">
          {isFetching && <span className="mr-3 animate-pulse">Loading…</span>}
          Total: <span className="font-medium tabular-nums">{total.toLocaleString()}</span>
        </div>
      </div>

      <div className="bg-surface border border-border rounded-lg p-3 mb-4 flex flex-wrap gap-2 items-center">
        <input
          type="text"
          value={search}
          onChange={(e) => onFilterChange(setSearch)(e.target.value)}
          placeholder="Search path / action / email…"
          className="border border-border-strong rounded px-2 py-1 text-sm min-w-[260px]"
        />
        <select
          value={section}
          onChange={(e) => onFilterChange(setSection)(e.target.value)}
          className="border border-border-strong rounded px-2 py-1 text-sm"
        >
          {SECTION_OPTIONS.map((s) => (
            <option key={s} value={s}>{s || 'All sections'}</option>
          ))}
        </select>
        <select
          value={method}
          onChange={(e) => onFilterChange(setMethod)(e.target.value)}
          className="border border-border-strong rounded px-2 py-1 text-sm"
        >
          {METHOD_OPTIONS.map((m) => (
            <option key={m} value={m}>{m || 'All methods'}</option>
          ))}
        </select>

        <div className="flex items-center gap-1 text-sm text-content-muted">
          <span className="text-xs text-content-subtle">From</span>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => onFilterChange(setDateFrom)(e.target.value)}
            className="border border-border-strong rounded px-2 py-1 text-sm"
          />
          <span className="text-xs text-content-subtle">To</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => onFilterChange(setDateTo)(e.target.value)}
            className="border border-border-strong rounded px-2 py-1 text-sm"
          />
          {(dateFrom || dateTo) && (
            <button
              type="button"
              onClick={() => { onFilterChange(setDateFrom)(''); onFilterChange(setDateTo)(''); }}
              className="text-xs text-info hover:underline ml-1"
            >clear</button>
          )}
        </div>

        <div className="ml-auto flex items-center gap-2 text-sm text-content-muted">
          <span>Per page:</span>
          <select
            value={limit}
            onChange={(e) => { setLimit(parseInt(e.target.value, 10)); setPage(1); }}
            className="border border-border-strong rounded px-2 py-1 text-sm"
          >
            {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </div>

      <div className="bg-surface border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface-muted text-xs uppercase text-content-subtle">
            <tr>
              <th className="px-3 py-2 text-left">Time</th>
              <th className="px-3 py-2 text-left">Actor</th>
              <th className="px-3 py-2 text-left">Method</th>
              <th className="px-3 py-2 text-left">Action / Path</th>
              <th className="px-3 py-2 text-left">Section</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => <Row key={r.id} row={r} />)}
            {!isLoading && rows.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-content-subtle">No audit entries match your filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-between text-sm">
        <div className="text-content-subtle">
          Page <span className="font-medium">{page}</span> / {totalPages}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setPage(1)}
            disabled={page === 1}
            className="px-3 py-1 border border-border-strong rounded hover:bg-surface-muted disabled:opacity-50"
          >First</button>
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1 border border-border-strong rounded hover:bg-surface-muted disabled:opacity-50"
          >Prev</button>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="px-3 py-1 border border-border-strong rounded hover:bg-surface-muted disabled:opacity-50"
          >Next</button>
          <button
            onClick={() => setPage(totalPages)}
            disabled={page >= totalPages}
            className="px-3 py-1 border border-border-strong rounded hover:bg-surface-muted disabled:opacity-50"
          >Last</button>
        </div>
      </div>
    </div>
  );
}
